import React, { useState } from "react";
import { api, type Me } from "./api";
import { FLAG_LABEL, REVIEW_LABEL, HOLD_LABEL } from "./ReviewQueue";
import { eastern, type Person } from "./RecordScreen";
import type { DocRequest } from "./RecordFollowUp";

/**
 * Review, flags and comments on one record (build plan §7.1 #4, #5). Every
 * action adds a note; nothing is edited or removed, so the history below is
 * the whole story. Buttons appear only for what this user may do (plan §5);
 * the server checks again.
 */

export interface Note {
  noteKey: string;
  noteType: string;
  flagType: string;
  body: string;
  relatedKey: string;
  authorEmail: string;
  authorName: string;
  createdUtc: string;
  status?: string;
}

interface ActionResult {
  person: Person;
  notes: Note[];
  docRequests?: DocRequest[];
}

const FLAG_TYPES = ["MissingDocument", "ClarificationNeeded", "ClinicalConcern", "NeedsSupervisorReview", "ExpectedAtMedStation", "AttendanceHoldRequested"] as const;
const FLAG_CAP: Record<string, string> = {
  MissingDocument: "record.comment",
  ClarificationNeeded: "record.comment",
  ClinicalConcern: "record.flagClinical",
  NeedsSupervisorReview: "record.flagClinical",
  ExpectedAtMedStation: "record.flagClinical",
  // Plan §7.1 #7: med staff ask the Director to decide; a reason is required.
  AttendanceHoldRequested: "record.flagClinical",
};

const NOTE_LABEL: Record<string, string> = {
  Comment: "Comment",
  FlagSet: "Flag set",
  FlagCleared: "Flag cleared",
  Reviewed: "Marked reviewed",
  ReReviewRequested: "Re-review requested",
  DocRequestSent: "Document request sent",
  DocRequestReminder: "Reminder sent to the family",
  DocRequestClosed: "Document request marked done",
  HoldDecision: "Attendance hold",
};

/** A note whose Body is one JSON object (document requests, hold decisions), or null. */
function jsonOf(body: string): Record<string, unknown> | null {
  if (!body.startsWith("{")) return null;
  try {
    return JSON.parse(body) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** The note's text as staff read it. */
function NoteBody({ n }: { n: Note }) {
  const j = jsonOf(n.body);
  if (n.noteType === "DocRequestSent" && j) {
    return (
      <p className="break-words">
        {j.template === "clarify" ? "Please check" : "Please upload"}: <span className="font-medium">{String(j.itemLabel ?? j.item ?? "")}</span>
        {Array.isArray(j.to) && <span className="text-slate-600"> — to {(j.to as string[]).join(", ")}</span>}
        {j.staffNote ? <span className="block text-slate-600">Staff note: {String(j.staffNote)}</span> : null}
      </p>
    );
  }
  if (n.noteType === "DocRequestReminder" && j) {
    return Array.isArray(j.to) ? <p className="break-words text-slate-600">To {(j.to as string[]).join(", ")}</p> : null;
  }
  if (n.noteType === "HoldDecision" && j) {
    const log = Array.isArray(j.consultations) ? (j.consultations as { method: string; with: string; summary: string; onDate?: string }[]) : [];
    return (
      <div className="break-words">
        {j.decision ? <p className="font-semibold">Decision: {HOLD_LABEL[String(j.decision)] ?? String(j.decision)}</p> : <p className="font-medium">Consultation logged</p>}
        {j.conditions ? <p>Conditions: {String(j.conditions)}</p> : null}
        {j.summary ? <p className="whitespace-pre-wrap">{String(j.summary)}</p> : null}
        {log.length > 0 && (
          <ul className="mt-1 list-disc pl-5 text-slate-700">
            {log.map((c, i) => (
              <li key={i}>
                {c.method} with {c.with}
                {c.onDate ? " (" + c.onDate + ")" : ""}: <span className="whitespace-pre-wrap">{c.summary}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }
  return n.body ? <p className="whitespace-pre-wrap break-words">{n.body}</p> : null;
}

function useAction(personKey: string, onDone: (r: ActionResult) => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function run(path: string, body: unknown): Promise<boolean> {
    setBusy(true);
    setError(null);
    const r = await api<ActionResult>("POST", `/api/records/${encodeURIComponent(personKey)}/${path}`, body);
    setBusy(false);
    if (!r.ok) {
      setError(r.data.error ?? "That didn't save.");
      return false;
    }
    onDone(r.data);
    return true;
  }
  return { busy, error, run };
}

function ReviewBox({ me, person, onDone }: { me: Me; person: Person; onDone: (r: ActionResult) => void }) {
  const { busy, error, run } = useAction(person.personKey, onDone);
  const [note, setNote] = useState("");
  const status = person.reviewStatus || "NotReviewed";
  const has = (c: string) => me.capabilities.includes(c);
  const canMark = status === "NotReviewed" || status === "FamilyUpdated" ? has("record.markReviewed") : has("record.reReview");
  const markLabel = status === "Reviewed" ? "Re-review: mark reviewed again" : status === "ReReviewRequested" ? "Complete the re-review" : "Mark reviewed";
  const canAsk = (status === "Reviewed" || status === "FamilyUpdated") && has("record.markReviewed");
  if (!canMark && !canAsk) {
    return (
      <section className="section-card">
        <h3 className="mb-1 text-base font-semibold">Review</h3>
        <p className="text-sm text-slate-700">Status: {REVIEW_LABEL[status] ?? status}.</p>
      </section>
    );
  }
  return (
    <section className="section-card">
      <h3 className="mb-1 text-base font-semibold">Review</h3>
      <p className="mb-3 text-sm text-slate-700">
        Status: <span className="font-medium">{REVIEW_LABEL[status] ?? status}</span>
        {person.familyUpdatedAfterReview && <span className="ml-2 text-amber-900">The family changed the application after the last review.</span>}
      </p>
      <label htmlFor="review-note" className="field-label">Note (optional for a review; required to ask for a re-review)</label>
      <textarea id="review-note" className="field-input min-h-20" value={note} onChange={(e) => setNote(e.target.value)} maxLength={4000} />
      {error && <p className="field-error" role="alert">{error}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        {canMark && (
          <button type="button" className="btn-primary" disabled={busy} onClick={async () => (await run("review", { action: "markReviewed", note })) && setNote("")}>
            {markLabel}
          </button>
        )}
        {canAsk && (
          <button type="button" className="btn-secondary" disabled={busy || !note.trim()} onClick={async () => (await run("review", { action: "requestReReview", note })) && setNote("")}>
            Ask a Supervisor to re-review
          </button>
        )}
      </div>
    </section>
  );
}

function FlagsBox({ me, person, onDone }: { me: Me; person: Person; onDone: (r: ActionResult) => void }) {
  const { busy, error, run } = useAction(person.personKey, onDone);
  const [reason, setReason] = useState("");
  const isSet = (t: string) => (t === "ExpectedAtMedStation" ? person.expectedReasons.includes("Flagged") : person.flags.some((f) => f.type === t));
  const needsReason = (t: string, on: boolean) => t === "AttendanceHoldRequested" && !on;
  const mine = FLAG_TYPES.filter((t) => me.capabilities.includes(FLAG_CAP[t]));
  return (
    <section className="section-card">
      <h3 className="mb-1 text-base font-semibold">Flags</h3>
      <label htmlFor="flag-reason" className="field-label">Reason (optional, kept with the flag)</label>
      <input id="flag-reason" className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={4000} />
      {error && <p className="field-error" role="alert">{error}</p>}
      <ul className="mt-3 divide-y divide-slate-100">
        {FLAG_TYPES.map((t) => {
          const on = isSet(t);
          const allowed = mine.includes(t);
          return (
            <li key={t} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
              <span>
                {t === "ExpectedAtMedStation" ? "Expected at med station (by hand)" : t === "AttendanceHoldRequested" ? "Attendance hold: ask the Director to decide (reason required)" : FLAG_LABEL[t]}
                {on && <span className="ml-2 rounded bg-red-50 px-1.5 py-0.5 text-xs font-medium text-red-900">Set</span>}
              </span>
              {allowed ? (
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={busy || (needsReason(t, on) && !reason.trim())}
                  onClick={async () => (await run("flags", { type: t, action: on ? "clear" : "set", reason })) && setReason("")}
                >
                  {on ? "Clear" : "Set"}
                </button>
              ) : (
                <span className="text-xs text-slate-500">{on ? "" : "Not for your role"}</span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function NoteLine({ n }: { n: Note }) {
  return (
    <div className="text-sm">
      <p className="text-xs text-slate-500">
        <span className="font-medium text-slate-700">{n.authorName || n.authorEmail}</span> · {eastern(n.createdUtc)}
        {n.noteType !== "Comment" && (
          <> · <span className="font-medium">{NOTE_LABEL[n.noteType] ?? n.noteType}{n.flagType ? `: ${FLAG_LABEL[n.flagType] ?? n.flagType}` : ""}</span></>
        )}
      </p>
      <NoteBody n={n} />
    </div>
  );
}

function CommentsBox({ me, personKey, notes, onDone }: { me: Me; personKey: string; notes: Note[]; onDone: (r: ActionResult) => void }) {
  const { busy, error, run } = useAction(personKey, onDone);
  const [body, setBody] = useState("");
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [reply, setReply] = useState("");
  const canComment = me.capabilities.includes("record.comment");
  const keys = new Set(notes.map((n) => n.noteKey));
  const top = notes.filter((n) => !n.relatedKey || !keys.has(n.relatedKey));
  const repliesTo = (k: string) => notes.filter((n) => n.relatedKey === k);
  return (
    <section className="section-card">
      <h3 className="mb-3 text-base font-semibold">Comments and history</h3>
      {notes.length === 0 && <p className="text-sm text-slate-600">Nothing yet.</p>}
      <ol className="space-y-3">
        {top.map((n) => (
          <li key={n.noteKey} className={`rounded-md border p-3 ${n.noteType === "Comment" ? "border-slate-200" : "border-slate-100 bg-slate-50"}`}>
            <NoteLine n={n} />
            {repliesTo(n.noteKey).length > 0 && (
              <ol className="mt-2 space-y-2 border-l-2 border-slate-200 pl-3">
                {repliesTo(n.noteKey).map((r) => (
                  <li key={r.noteKey}><NoteLine n={r} /></li>
                ))}
              </ol>
            )}
            {canComment && (
              replyTo === n.noteKey ? (
                <div className="mt-2">
                  <label htmlFor={`reply-${n.noteKey}`} className="sr-only">Reply</label>
                  <textarea id={`reply-${n.noteKey}`} className="field-input min-h-16" value={reply} onChange={(e) => setReply(e.target.value)} maxLength={4000} />
                  <div className="mt-2 flex gap-2">
                    <button type="button" className="btn-primary" disabled={busy || !reply.trim()} onClick={async () => { if (await run("comments", { body: reply, replyTo: n.noteKey })) { setReply(""); setReplyTo(null); } }}>
                      Reply
                    </button>
                    <button type="button" className="btn-secondary" onClick={() => setReplyTo(null)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <button type="button" className="mt-2 text-xs text-program underline" onClick={() => { setReplyTo(n.noteKey); setReply(""); }}>Reply</button>
              )
            )}
          </li>
        ))}
      </ol>
      {canComment && (
        <div className="mt-4">
          <label htmlFor="new-comment" className="field-label">Add a comment</label>
          <textarea id="new-comment" className="field-input min-h-20" value={body} onChange={(e) => setBody(e.target.value)} maxLength={4000} />
          {error && <p className="field-error" role="alert">{error}</p>}
          <button type="button" className="btn-primary mt-2" disabled={busy || !body.trim()} onClick={async () => (await run("comments", { body })) && setBody("")}>
            Add comment
          </button>
          <p className="field-hint mt-1">Comments are kept as written and can't be edited or removed.</p>
        </div>
      )}
    </section>
  );
}

export function ReviewPanel({
  me,
  person,
  notes,
  onChange,
  middle,
}: {
  me: Me;
  person: Person;
  notes: Note[];
  onChange: (r: ActionResult) => void;
  /** Between the flags and the history: the attendance hold and document requests. */
  middle?: React.ReactNode;
}): React.ReactElement {
  return (
    <>
      <ReviewBox me={me} person={person} onDone={onChange} />
      <FlagsBox me={me} person={person} onDone={onChange} />
      {middle}
      <CommentsBox me={me} personKey={person.personKey} notes={notes} onDone={onChange} />
    </>
  );
}
